import { useContext, useState } from 'react';
import { text } from '../app/i18n';
import { Dialog, DialogActions } from '../design-system/Overlay';
import { formatValue, holds, targetState, visible, withComputed, type Answers, type ConformityRules, type Entry, type Inputs, type Section } from './form-targets';
import { ConformityRulesContext, TemplateEntry } from './TemplateEntry';
import { useRowEntries } from './form-reactions-context';

/**
 * Série ouverte (inspection-template 1.2.0, ADR-141 §1.2) : des lignes « N° n » ajoutées une à
 * une, un badge par ligne (dans la cible, pas encore, hors cible, non conforme), et la décision de sortie quand la
 * dernière ligne satisfait `exit.when` — la ligne retenue porte `exit: true`. Le serveur relit le
 * nombre de lignes, l'unicité et la validité de la sortie (`FORM_SERIES_*`) ; ses refus s'affichent ici.
 */

export type SeriesRow = Answers & { exit?: boolean };
type SeriesSectionType = Section & { series: NonNullable<Section['series']> };

function ownValues(row: SeriesRow): Answers {
  return Object.fromEntries(Object.entries(row).filter(([key]) => key !== 'exit'));
}

export type RowState = 'in' | 'wait' | 'out' | 'nonconform' | 'empty';

/**
 * Pastille d'une ligne de série, comme la tablette (`seriesRowState` de `form_rules.dart`) : hors
 * cible l'emporte sur non conforme, qui l'emporte sur « pas encore », qui l'emporte sur « dans la
 * cible ». « Dans la cible » ne parle que des mesures qui ont une cible : une ligne qui n'a qu'une
 * pesée ou une réponse conforme n'a pas de pastille ; une cible non résolue ne donne aucun verdict.
 */
export function seriesRowState(fields: readonly Entry[], values: Answers, inputs: Inputs, rules: ConformityRules = {}): RowState {
  let out = false;
  let nonconform = false;
  let wait = false;
  let inTarget = false;
  for (const entry of fields) {
    const current = targetState(entry, values[entry.field.field_id], values, inputs, rules);
    if (current === 'out') out = true;
    else if (current === 'nonconform') nonconform = true;
    else if (current === 'wait') wait = true;
    else if (current === 'in' && entry.target !== undefined) inTarget = true;
  }
  return out ? 'out' : nonconform ? 'nonconform' : wait ? 'wait' : inTarget ? 'in' : 'empty';
}

const BADGE: Readonly<Record<Exclude<RowState, 'empty'>, { label: string; tone: string }>> = {
  in: { label: 'Dans la cible', tone: 'success' },
  wait: { label: 'Pas encore dans la cible', tone: 'warning' },
  out: { label: 'Hors cible', tone: 'danger' },
  nonconform: { label: 'Non conforme', tone: 'danger' },
};

/** Ligne d'une série : « N° 1 », comme la tablette (`seriesRow`) ; une série ne porte pas que des tests. */
const rowLabel = (index: number): string => `N° ${index + 1}`;
/** Bouton d'ajout, comme la tablette (`seriesAdd`) ; le gabarit ne déclare pas de libellé (`add_button` : `top` ou `none`). */
const ADD_ROW = 'Ajouter une ligne';

export function SeriesSection({ section, rows, onChange, disabled, errors, inputs, warnings = {} }: { section: SeriesSectionType; rows: SeriesRow[]; onChange: (next: SeriesRow[]) => void; disabled: boolean; errors: Readonly<Record<string, string>>; inputs: Inputs; /** Avertissements par chemin de réponse (addendum d'ADR-126) : sous le champ, et un badge sur la ligne. */ warnings?: Readonly<Record<string, readonly string[]>> }) {
  const [asking, setAsking] = useState(false);
  const rules = useContext(ConformityRulesContext);
  // Choix et filtre rechargés par la ligne elle-même (ADR-146 addendum (21)).
  const rowFields = useRowEntries();
  const { max_count: max, exit } = section.series;
  const sid = section.section_id;
  const exited = rows.some((row) => row.exit === true);
  const last = rows.at(-1);
  const lastValues = last === undefined ? {} : withComputed(section.fields, ownValues(last), inputs);
  // `exit.when` se lit sur les réponses de la ligne seulement, comme au serveur.
  const canExit = exit !== undefined && last !== undefined && !exited && Object.keys(ownValues(last)).length > 0 && holds(exit.when, lastValues);
  const update = (index: number, patch: (row: SeriesRow) => SeriesRow): void => onChange(rows.map((row, position) => (position === index ? patch(row) : row)));

  return (
    <div className="ay-series" aria-label={text(section.label, 'fr', sid)}>
      {rows.map((row, index) => {
        const raw = ownValues(row);
        const values = withComputed(section.fields, raw, inputs);
        const state = seriesRowState(section.fields, values, inputs, rules);
        const exitError = errors[`answers.${sid}.${index}.exit`];
        const exitInvalid = row.exit === true && exit !== undefined && !holds(exit.when, values);
        const warned = Object.keys(warnings).some((path) => path.startsWith(`answers.${sid}.${index}.`));
        return (
          <fieldset key={index} className="ay-form__section ay-series__row" aria-label={rowLabel(index)}>
            <legend className="ay-series__row-header">
              <span>{rowLabel(index)}</span>
              {state !== 'empty' && <span className={`ay-badge ay-badge--${BADGE[state].tone}`}>{BADGE[state].label}</span>}
              {row.exit === true && <span className="ay-badge ay-badge--success">Sortie</span>}
              {warned && <span className="ay-badge ay-badge--warning">Avertissement</span>}
            </legend>
            {rowFields(section.fields, sid, index)
              .filter((entry) => entry.compute !== undefined || visible(entry.visible_when, raw))
              .map((entry) => {
                const id = entry.field.field_id;
                const required = entry.field.required === true || (entry.required_when !== undefined && visible(entry.required_when, raw));
                const error = errors[`answers.${sid}.${index}.${id}`];
                const warning = warnings[`answers.${sid}.${index}.${id}`];
                return (
                  <TemplateEntry
                    key={id}
                    entry={entry}
                    values={values}
                    inputs={inputs}
                    controlId={`${sid}-${index}-${id}`}
                    path={`answers.${sid}.${index}.${id}`}
                    required={required}
                    disabled={disabled}
                    {...(error === undefined ? {} : { error })}
                    {...(warning === undefined ? {} : { warnings: warning })}
                    onChange={(next) => update(index, (current) => ({ ...current, [id]: next }))}
                    onFill={(filled) => update(index, (current) => ({ ...current, ...filled }))}
                  />
                );
              })}
            {exitInvalid && <span className="ay-field__error">Cette ligne ne permet plus la sortie.</span>}
            {exitError !== undefined && <span className="ay-field__error">{exitError}</span>}
            {index === rows.length - 1 && (
              <div className="ay-series__actions">
                {row.exit === true ? (
                  <button type="button" className="ay-button ay-button--secondary" disabled={disabled} onClick={() => update(index, (current) => ownValues(current))}>
                    Annuler la sortie
                  </button>
                ) : (
                  <button type="button" className="ay-button ay-button--secondary" disabled={disabled} onClick={() => onChange(rows.slice(0, -1))}>
                    Retirer cette ligne
                  </button>
                )}
              </div>
            )}
          </fieldset>
        );
      })}
      <div className="ay-series__actions">
        <button type="button" className="ay-button ay-button--secondary" disabled={disabled || exited || (max !== undefined && rows.length >= max)} onClick={() => onChange([...rows, {}])}>
          {ADD_ROW}
        </button>
        {canExit && (
          <button type="button" className="ay-button" disabled={disabled} onClick={() => setAsking(true)}>
            Sortir…
          </button>
        )}
      </div>
      {errors[`answers.${sid}`] !== undefined && <span className="ay-field__error">{errors[`answers.${sid}`]}</span>}
      {asking && exit !== undefined && last !== undefined && (
        <Dialog
          title={text(exit.prompt, 'fr', 'Sortir de la série ?')}
          subtitle={`${rowLabel(rows.length - 1)} · dans la cible`}
          open
          onClose={() => setAsking(false)}
          size="sm"
          footer={
            <DialogActions
              cancel={{ label: 'Pas encore', onClick: () => setAsking(false) }}
              primary={{
                label: 'Sortir',
                onClick: () => {
                  setAsking(false);
                  update(rows.length - 1, (current) => ({ ...current, exit: true }));
                },
              }}
            />
          }
        >
          <ul className="ay-deviations">
            {section.fields
              .filter((entry) => lastValues[entry.field.field_id] !== undefined && lastValues[entry.field.field_id] !== null)
              .map((entry) => (
                <li key={entry.field.field_id}>
                  <span className="ay-deviations__label">{text(entry.field.label, 'fr', entry.field.field_id)}</span>
                  <span>{formatValue(entry, lastValues[entry.field.field_id])}</span>
                </li>
              ))}
          </ul>
          <p className="ay-text">La sortie retient cette ligne ; validez ensuite le formulaire.</p>
        </Dialog>
      )}
    </div>
  );
}
